import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import Button from '../../../componentsv2/Button';
import { ArrowRightIcon } from '../../../components/Icons';
import {
  downloadDocumentsCompanyUser,
  getCompanyCompliance,
  getDocuments,
} from '../../../services/compliance';
import { Language } from '../../../services/types';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { Company } from '../../../types/companyUser';
import { Nullable } from '../../../types';
import { Compliance, Document } from '../../../types/compliance';
import { COLORS } from '../../../themev2';
import { COMPLIANCE } from '../../../constants/auth';
import { Link } from 'react-router-dom';
import DocumentGroup from '../../../componentsv2/DocumentArea/DocumentGroup';
import { getGroupNameAndFileName } from '../../../utils/helpers';
import { contentTypeToExtensionMap } from '../../../utils/common';
import { useLazyTranslation } from '../../../hooks/i18n';
import { useSnackbar } from '../../../context';
import { Box, Divider } from '@mui/material';
import Skeleton from '../../../componentsv2/Skeleton';

export default function ComplianceCard({ selectedCompany }: { selectedCompany: Company }) {
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['compliance', 'client']);
  const { showSnackbar } = useSnackbar();
  const { language } = useUserLocation();
  const isEnabled = selectedCompany.productStatus.readyForCompliance;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [compliance, setCompliance] = useState<Nullable<Compliance>>(null);
  const [topTwoDocuments, setTopTwoDocuments] = useState<Document[]>([]);

  useEffect(() => {
    if (isEnabled) {
      setIsLoading(true);
      getCompanyCompliance(selectedCompany.id)
        .then((compliance) => {
          setCompliance(compliance);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedCompany?.id, isEnabled]);

  useEffect(() => {
    if (compliance?.id) {
      setIsLoadingDocuments(true);
      getDocuments(selectedCompany?.id!, compliance.id, language as Language, 1, 2)
        .then((documents) => {
          setTopTwoDocuments(documents.results);
        })
        .finally(() => {
          setIsLoadingDocuments(false);
        });
    }
  }, [compliance?.id, language, selectedCompany?.id]);

  const handleDownloadDocument = (documentName: string, documentId: number) => {
    downloadDocumentsCompanyUser(selectedCompany?.id!, compliance?.id!, documentId)
      .then((response) => {
        const contentType = response.headers['content-type'];
        const blob = response.data;

        const extension = contentTypeToExtensionMap[contentType] || '.bin';

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${documentName}${extension}`;
        document.body.appendChild(a);

        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSnackbar(t('compliance:successMessage.download'), 'success');
      })
      .catch(() => {
        showSnackbar(t('compliance:errorMessage.generic'), 'error');
      });
  };

  const isLoadingCompliance = isLoading || isLoadingDocuments || isLoadingTranslations;

  return (
    <Section
      isFullHeight
      sx={{
        background: COLORS.brand.white,
        boxShadow: '0px 2px 10px 0px rgba(156, 171, 194, 0.10)',
        padding: '1em 2em',
      }}
    >
      <Grid container rowSpacing={1}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'start', md: 'space-between' },
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Typography
            sx={{ color: COLORS.brand.night, fontSize: '18px', lineHeight: '150%', fontWeight: 600 }}
          >
            {t('common:compliance.card.title')}
          </Typography>
          <Link to={COMPLIANCE} style={{ textDecoration: 'none' }}>
            <Button color="tertiary" endIcon={<ArrowRightIcon />} type="text" fullWidth>
              {t('common:compliance.card.viewAllBtn')}
            </Button>
          </Link>
        </Grid>
        <Grid item xs={12}>
          {isLoadingCompliance ? (
            <Skeleton variant="rectangular" animation="wave" height={120} />
          ) : !!topTwoDocuments.length ? (
            <Box>
              {topTwoDocuments.map((document) => {
                const { name, fileName } = getGroupNameAndFileName(document);
                return (
                  <DocumentGroup
                    key={document.id}
                    name={name}
                    linkName={`${document.assetAmount} ${t('compliance:documents.labels.files')}`}
                    linkAction={() => handleDownloadDocument(fileName, document.id)}
                    onClickDownload={() => handleDownloadDocument(fileName, document.id)}
                  />
                );
              })}
            </Box>
          ) : (
            <Grid container rowSpacing={1}>
              <Grid item xs={12} sx={{ marginBottom: '1em' }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={700} textAlign="center">
                  {t('compliance:clientSide.documents.labels.noDocuments')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" textAlign="center">
                  {t('compliance:clientSide.documents.labels.noDocumentsDescription')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Section>
  );
}
